import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { columns } from './data-table-column'
import DataTableToolbar from './data-table-toolbar'

const PAGE_SIZE = 200
const SEARCH_DEBOUNCE_MS = 1000

const CommTaglibs = () => {
  const [level, setLevelState] = useState(null)
  const [order, setOrderState] = useState('frameCount')
  const [after, setAfter] = useState(null)
  const [afters, setAfters] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const searchRef = useRef(search)
  searchRef.current = search

  const resetPage = useCallback(() => {
    setAfter(null)
    setAfters([])
  }, [])

  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchInput.trim()
      if (next === searchRef.current) return
      setAfter(null)
      setAfters([])
      setSearch(next)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchInput])

  const setLevel = useCallback(
    (value) => {
      setLevelState(value)
      resetPage()
    },
    [resetPage],
  )

  const setOrder = useCallback(
    (value) => {
      setOrderState(value)
      resetPage()
    },
    [resetPage],
  )

  const goAfter = useCallback((nextAfter) => {
    if (nextAfter === null) {
      setAfter(null)
      return
    }
    setAfters((prev) => {
      const index = prev.findIndex((a) => a === nextAfter)
      if (index === -1) return [...prev, nextAfter]
      return prev
    })
    setAfter(nextAfter)
  }, [])

  const params = {
    order,
    ...(level != null ? { level } : {}),
    ...(after ? { after } : {}),
    ...(search ? { q: search } : {}),
  }

  const { data, error, isLoading, mutate } = useSWR(['/taglibs', params], {
    keepPreviousData: true,
  })

  const nextAfter =
    data?.length >= PAGE_SIZE ? data[data.length - 1]?.id : undefined

  const table = useReactTable({
    data: data ?? [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex-shrink-0">
        <DataTableToolbar
          mutate={mutate}
          level={level}
          onLevelChange={setLevel}
          order={order}
          onOrderChange={setOrder}
          after={after}
          afters={afters}
          nextAfter={nextAfter}
          goAfter={goAfter}
          search={searchInput}
          onSearchChange={setSearchInput}
        />
      </div>
      {error ? (
        <div className="mb-2 flex-shrink-0 whitespace-pre-wrap rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error.message}
        </div>
      ) : null}
      <div className="min-h-0 flex-1">
        {isLoading && !data ? (
          <Loader />
        ) : !data ? (
          <EmptyDatas />
        ) : (
          <Card className="flex h-full flex-col">
            <CardContent className="flex-1 overflow-hidden pb-0 pl-4 pr-0">
              <div className="h-full overflow-auto">
                <Table table={table} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default CommTaglibs
